import { Stack, Text } from "@similarweb/ui-components/foundations";

import { useToasts } from "app/common/hooks";
import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpTrademarkNavigation } from "../../app/navigation";
import { useGetTrademarkById, usePatchTrademark } from "../../services/api/trademarks";
import { TrademarkPermissions } from "../../services/asset-permissions";
import { trademarkAssetTracking } from "../../services/tracking/asset-management";
import type { ITradeMarkFormData } from "../../shared/components/forms/trademark-config-form/form-types";
import { TrademarkConfigsForm } from "../../shared/components/forms/trademark-config-form/trademark-config-form";
import {
    trademarkConvertFromFormData,
    trademarkConvertToFormData,
} from "../../shared/components/forms/trademark-config-form/utils";
import { useMonitorAndProtectTrademarkDataAndParams } from "../../shared/hooks/trademark-params";
import { useSelectTrademark } from "../../shared/hooks/use-select-trademark";
import { Widget } from "../../shared/ui/layout/widget";
import { Toast } from "../../shared/ui/toast";

import { DeleteTrademark } from "./trademark-delete-modal";

export const TrademarkConfigsEditPage = () => {
    const toasts = useToasts();
    const translate = useTranslation();
    const { trademarkId } = useMonitorAndProtectTrademarkDataAndParams();
    const {
        data: trademarkDetail,
        isLoading: isGetLoading,
        refetch: refetchTrademark,
    } = useGetTrademarkById(trademarkId);
    const { mutateAsync: patchTrademark, isPending: isPatchPending } = usePatchTrademark();
    const { selectTrademarkAndRedirect } = useSelectTrademark();
    const permissions = new TrademarkPermissions(trademarkDetail);
    const readOnlyMode = permissions.canView && permissions.canEdit === false;

    const handleSaveConfig = async (newFormData: ITradeMarkFormData) => {
        try {
            const payload = trademarkConvertFromFormData(newFormData);
            await patchTrademark([trademarkId, payload]);
            trademarkAssetTracking.onUpdated({
                id: trademarkId,
                name: newFormData.name,
                variationsCount: payload.variationsAndTypos?.length,
            });
            // Wait for the trademark detail to refetch before navigating
            const { data: updatedTrademark } = await refetchTrademark();
            toasts.showSuccessToast(
                <Toast text={translate("monitor_and_protect.features.trademark_config_edit.success_toast")} />,
            );
            if (updatedTrademark) {
                selectTrademarkAndRedirect(updatedTrademark);
            } else {
                void mnpTrademarkNavigation.violations.go({
                    trademarkId,
                });
            }
        } catch {
            toasts.showErrorToast(
                <Toast
                    type="error"
                    text={translate("monitor_and_protect.features.trademark_config_edit.error_toast")}
                />,
            );
        }
    };

    const handleCancelEdit = () => {
        void mnpTrademarkNavigation.trademarks.go();
    };

    return (
        <Stack
            paddingX="80px"
            paddingY="32px"
            alignItems="center"
        >
            <Widget width="796px">
                <Text
                    fontVariant="h4"
                    padding="24px 24px 0px"
                >
                    {translate("monitor_and_protect.features.trademark_config_edit.title")}
                </Text>
                <TrademarkConfigsForm
                    key={trademarkDetail?.lastUpdated}
                    readonly={readOnlyMode}
                    initialData={trademarkConvertToFormData(trademarkDetail)}
                    onSave={(data) => void handleSaveConfig(data)}
                    onCancel={handleCancelEdit}
                    isLoading={isGetLoading || isPatchPending}
                    cancelButtonText={translate(readOnlyMode ? "global.back" : "global.cancel")}
                    confirmButtonTooltipText={translate(
                        readOnlyMode
                            ? "monitor_and_protect.features.trademark_config_edit.read_only_tooltip"
                            : "monitor_and_protect.features.trademark_config_edit.save_changes_tooltip",
                    )}
                    footerActions={
                        readOnlyMode ? null : (
                            <DeleteTrademark
                                canDelete={permissions.canDelete}
                                trademarkId={trademarkId}
                                trademarkName={trademarkDetail?.name}
                                domain={trademarkDetail?.domain}
                            />
                        )
                    }
                />
            </Widget>
        </Stack>
    );
};
